/**
 * ASC 340-40 Phase 12d — Impairment Test Panel (12c).
 *
 * Per-contract:
 *   - Manual test trigger with rfc_estimate + rationale (340-40-35-6)
 *   - History timeline with PENDING / CONSUMED / CANCELLED /
 *     PASSED_NO_IMPAIRMENT / SKIPPED_IMMATERIAL / FAILED badges
 *   - Cancel action on PENDING rows
 *   - Consume action to drain all PENDING into JEs
 *
 * Backend:
 *   POST /contracts/{id}/test-impairment
 *   POST /contracts/{id}/impairment/{history_id}/cancel
 *   POST /contracts/{id}/impairment/consume
 *   GET /contracts/{id}/impairment-history
 */
import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount } from '../../constants';
import DataTable, { Column } from './DataTable';
import Modal from './Modal';
import { FormRow, InlineError, inputStyle, btnPrimary, btnSecondary } from './CostLedgerPanel';

const API = `${API_V1}/revenue`;

interface ImpairmentHistoryRow {
  id: number;
  impairment_type: string;
  status: string;
  test_period: string;
  total_carrying_before: number;
  remaining_consideration: number;
  remaining_fulfillment_costs: number;
  recoverable_amount: number;
  impairment_loss: number;
  materiality_threshold: number | null;
  reversal_of_history_id: number | null;
  reversal_cap: number | null;
  rationale: string;
  tested_by: number | null;
  tested_at: string | null;
  consumed_at: string | null;
  consumed_je_ids: string | null;
  cancelled_at: string | null;
  cancelled_by: number | null;
  cancellation_note: string | null;
  failed_reason: string | null;
}

interface Props {
  contractId: number;
  onStateChanged?: () => void;
}

export default function ImpairmentTestPanel({ contractId, onStateChanged }: Props) {
  const [rows, setRows] = useState<ImpairmentHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [testOpen, setTestOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<ImpairmentHistoryRow | null>(null);
  const [consumeBusy, setConsumeBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${API}/contracts/${contractId}/impairment-history?limit=200`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.history || []);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => { load(); }, [load]);

  const hasPending = rows.some((r) => r.status === 'PENDING');

  const consumePending = async () => {
    setError(null); setFlash(null); setConsumeBusy(true);
    try {
      const res = await authFetch(`${API}/contracts/${contractId}/impairment/consume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        const data = await res.json();
        setFlash(`Consumed ${data.consumed} pending row(s); ${data.failed || 0} failed.`);
        await load();
        if (onStateChanged) onStateChanged();
      } else {
        const err = await res.json().catch(() => ({ detail: 'Unknown error' }));
        const detail = typeof err.detail === 'string' ? err.detail : err.detail?.message || JSON.stringify(err.detail);
        setError(detail || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setError(e?.message || 'Network error');
    } finally {
      setConsumeBusy(false);
    }
  };

  const statusBadge = (s: string) => {
    let bg = 'rgba(100,116,139,0.15)';
    let color = 'var(--text-secondary)';
    if (s === 'PENDING') { bg = 'rgba(234,179,8,0.15)'; color = 'var(--warning)'; }
    else if (s === 'CONSUMED') { bg = 'rgba(34,197,94,0.15)'; color = 'var(--positive)'; }
    else if (s === 'FAILED') { bg = 'rgba(239,68,68,0.15)'; color = 'var(--negative)'; }
    return <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: bg, color }}>{s}</span>;
  };

  const cols: Column[] = [
    { key: 'tested_at', label: 'Tested', width: 140, render: (v: string | null) => v ? new Date(v).toLocaleString() : '-' },
    { key: 'impairment_type', label: 'Type' },
    { key: 'status', label: 'Status', render: (v: string) => statusBadge(v) },
    { key: 'test_period', label: 'Period', width: 90 },
    { key: 'total_carrying_before', label: 'Carrying', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'recoverable_amount', label: 'Recoverable', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'impairment_loss', label: 'Δ Loss', align: 'right',
      render: (v: number, row: ImpairmentHistoryRow) => (
        <span style={{ color: row.impairment_type === 'REVERSAL' ? 'var(--positive)' : (v > 0 ? 'var(--negative)' : 'var(--text-secondary)'), fontWeight: 600 }}>
          {formatAmount(v)}
        </span>
      ),
    },
    { key: 'rationale', label: 'Rationale', render: (v: string) => <span style={{ fontSize: 11 }}>{(v || '').slice(0, 60)}{(v || '').length > 60 ? '...' : ''}</span> },
    {
      key: 'id', label: '',
      render: (_v: number, row: ImpairmentHistoryRow) =>
        row.status === 'PENDING' ? (
          <button type="button" onClick={() => setCancelTarget(row)}
            style={{ padding: '3px 10px', fontSize: 11, fontWeight: 600, background: 'var(--negative)', color: 'white', border: 'none', borderRadius: 3, cursor: 'pointer' }}>
            Cancel
          </button>
        ) : null,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={() => setTestOpen(true)} style={btnPrimary}>
          Test Impairment
        </button>
        {hasPending && (
          <button type="button" onClick={consumePending} disabled={consumeBusy}
            style={{ ...btnPrimary, background: consumeBusy ? 'var(--bg-hover)' : 'var(--warning)' }}>
            {consumeBusy ? 'Consuming...' : 'Consume Pending'}
          </button>
        )}
      </div>

      {error && (
        <div style={{ padding: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid var(--negative)', borderRadius: 4, fontSize: 12, color: 'var(--negative)' }}>
          {error}
        </div>
      )}
      {flash && (
        <div style={{ padding: 10, background: 'rgba(34,197,94,0.1)', border: '1px solid var(--positive)', borderRadius: 4, fontSize: 12, color: 'var(--positive)' }}>
          {flash}
        </div>
      )}

      <DataTable columns={cols} rows={rows} loading={loading}
        emptyMessage="No impairment tests on this contract." rowKey="id" />

      <TestImpairmentModal open={testOpen} onClose={() => setTestOpen(false)}
        contractId={contractId}
        onSaved={async (msg) => { setTestOpen(false); setFlash(msg); await load(); if (onStateChanged) onStateChanged(); }} />

      {cancelTarget && (
        <CancelImpairmentModal row={cancelTarget} onClose={() => setCancelTarget(null)}
          contractId={contractId}
          onSaved={async (msg) => { setCancelTarget(null); setFlash(msg); await load(); if (onStateChanged) onStateChanged(); }} />
      )}
    </div>
  );
}

function TestImpairmentModal({ open, onClose, contractId, onSaved }: {
  open: boolean; onClose: () => void; contractId: number; onSaved: (msg: string) => void;
}) {
  const [rfc, setRfc] = useState('0');
  const [rationale, setRationale] = useState('');
  const [testPeriod, setTestPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr(null);
    const rfcVal = parseFloat(rfc);
    if (!(rfcVal >= 0)) { setErr('Remaining fulfillment costs estimate must be ≥ 0.'); return; }
    if (rationale.trim().length < 30) { setErr('Rationale must be at least 30 chars (SOX 404 audit trail).'); return; }
    setBusy(true);
    try {
      const res = await authFetch(`${API}/contracts/${contractId}/test-impairment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          remaining_fulfillment_costs_estimate: rfcVal,
          rationale: rationale.trim(),
          test_period: testPeriod,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        onSaved(`Impairment tested: status=${data.status} (history #${data.id}). ${data.impairment_loss ? `Δ ${formatAmount(data.impairment_loss)}` : ''}`);
        setRationale(''); setRfc('0');
      } else {
        const body = await res.json().catch(() => ({ detail: 'Unknown error' }));
        const detail = typeof body.detail === 'string' ? body.detail : body.detail?.message || JSON.stringify(body.detail);
        setErr(detail || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setErr(e?.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}
      title="Test Impairment (ASC 340-40-35-6)"
      description="Contract-level test. Carrying vs (remaining consideration − remaining fulfillment costs estimate). Stages PENDING / PASSED_NO_IMPAIRMENT / SKIPPED_IMMATERIAL history row."
      size="md"
      footer={
        <>
          <button type="button" onClick={onClose} style={btnSecondary} disabled={busy}>Cancel</button>
          <button type="button" onClick={submit} style={btnPrimary} disabled={busy}>
            {busy ? 'Testing...' : 'Run Test'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <FormRow label="Test Period (YYYY-MM)">
          <input type="text" value={testPeriod} onChange={(e) => setTestPeriod(e.target.value)} style={inputStyle} maxLength={7} />
        </FormRow>
        <FormRow label="Remaining Fulfillment Costs Estimate ($)">
          <input type="number" value={rfc} onChange={(e) => setRfc(e.target.value)} style={inputStyle} step="0.01" />
          <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2 }}>
            Reviewer estimate of costs still to incur. Default 0 = conservative (surfaces more impairments).
          </div>
        </FormRow>
        <FormRow label="Rationale (≥30 chars)">
          <textarea value={rationale} onChange={(e) => setRationale(e.target.value)}
            style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
            placeholder="Basis for the fulfillment cost estimate + recoverability assessment..." />
          <div style={{ fontSize: 10, color: rationale.length >= 30 ? 'var(--positive)' : 'var(--text-hint)', marginTop: 2 }}>
            {rationale.length}/30
          </div>
        </FormRow>
        {err && <InlineError>{err}</InlineError>}
      </div>
    </Modal>
  );
}

function CancelImpairmentModal({ row, onClose, contractId, onSaved }: {
  row: ImpairmentHistoryRow; onClose: () => void; contractId: number; onSaved: (msg: string) => void;
}) {
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr(null); setBusy(true);
    try {
      const res = await authFetch(`${API}/contracts/${contractId}/impairment/${row.id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note.trim() || null }),
      });
      if (res.ok) {
        onSaved(`Impairment #${row.id} cancelled.`);
      } else {
        const body = await res.json().catch(() => ({ detail: 'Unknown error' }));
        const detail = typeof body.detail === 'string' ? body.detail : body.detail?.message || JSON.stringify(body.detail);
        setErr(detail || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setErr(e?.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={true} onClose={onClose}
      title={`Cancel Impairment #${row.id}`}
      description={`Status PENDING — no JE posted. Δ ${formatAmount(row.impairment_loss)}`}
      size="sm"
      footer={
        <>
          <button type="button" onClick={onClose} style={btnSecondary} disabled={busy}>Keep</button>
          <button type="button" onClick={submit} style={{ ...btnPrimary, background: 'var(--negative)' }} disabled={busy}>
            {busy ? 'Cancelling...' : 'Cancel Impairment'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <FormRow label="Cancellation Note (optional)">
          <textarea value={note} onChange={(e) => setNote(e.target.value)}
            style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
            placeholder="e.g. Superseded by revised fulfillment cost estimate..." />
        </FormRow>
        {err && <InlineError>{err}</InlineError>}
      </div>
    </Modal>
  );
}
